import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Gift, Sparkles, Star, Zap, Heart, Calendar, Award, Ticket, LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { HomepageBanner } from "@/hooks/useHomepageBanners";

interface DynamicBannerProps {
  banner: HomepageBanner;
}

const iconMap: Record<string, LucideIcon> = {
  gift: Gift,
  sparkles: Sparkles,
  star: Star,
  zap: Zap,
  heart: Heart,
  calendar: Calendar,
  award: Award,
  ticket: Ticket,
};

const styleVariants: Record<string, { bg: string; iconBg: string; iconColor: string; textColor: string; subtextColor: string }> = {
  primary: {
    bg: "bg-gradient-to-r from-primary via-primary/90 to-secondary/30",
    iconBg: "bg-secondary/20",
    iconColor: "text-secondary",
    textColor: "text-primary-foreground",
    subtextColor: "text-primary-foreground/70",
  },
  secondary: {
    bg: "bg-gradient-to-r from-secondary via-secondary/90 to-primary/30",
    iconBg: "bg-primary/20",
    iconColor: "text-primary",
    textColor: "text-secondary-foreground",
    subtextColor: "text-secondary-foreground/70",
  },
  success: {
    bg: "bg-gradient-to-r from-success via-success/90 to-success/50",
    iconBg: "bg-success-foreground/20",
    iconColor: "text-success-foreground",
    textColor: "text-success-foreground",
    subtextColor: "text-success-foreground/70",
  },
  accent: {
    bg: "bg-gradient-to-r from-accent via-accent/90 to-accent/50",
    iconBg: "bg-accent-foreground/20",
    iconColor: "text-accent-foreground",
    textColor: "text-accent-foreground",
    subtextColor: "text-accent-foreground/70",
  },
};

const DynamicBanner = ({ banner }: DynamicBannerProps) => {
  const navigate = useNavigate();
  const IconComponent = iconMap[banner.icon] || Gift;
  const style = styleVariants[banner.style_variant] || styleVariants.primary;

  const handleClick = () => {
    if (banner.cta_link) {
      if (banner.cta_link.startsWith('http')) {
        window.open(banner.cta_link, '_blank');
      } else {
        navigate(banner.cta_link);
      }
    }
  };

  const content = (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative overflow-hidden rounded-lg ${style.bg} p-4 shadow-lg cursor-pointer`}
      onClick={handleClick}
    >
      <div className="absolute -right-4 -top-4 opacity-10">
        <IconComponent size={120} />
      </div>
      
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`rounded-full ${style.iconBg} p-2`}>
            <IconComponent className={`h-6 w-6 ${style.iconColor}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold ${style.textColor}`}>
                {banner.title}
              </h3>
              {banner.badge_text && (
                <Badge variant="secondary" className="text-xs">
                  {banner.badge_text}
                </Badge>
              )}
            </div>
            {banner.subtitle && (
              <p className={`text-sm ${style.subtextColor}`}>
                {banner.subtitle}
              </p>
            )}
          </div>
        </div>
        <ChevronRight className={`h-5 w-5 ${style.subtextColor}`} />
      </div>
    </motion.div>
  );

  // If it's an internal link, wrap in Link for better routing
  if (banner.cta_link && !banner.cta_link.startsWith('http')) {
    return <Link to={banner.cta_link}>{content}</Link>;
  }

  return content;
};

export default DynamicBanner;
